import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../../../utils/axios';

const initialState = {
    loading: false,
    invitations: [],
    inviteLinkUserDetails: null
};

const invitationSlice = createSlice({
    name: 'invitation',
    initialState,
    reducers: {
        setInviteLinkUserDetails: (state, action) => {
            state.inviteLinkUserDetails = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(createInvitations.fulfilled, (state, action) => {
                const newInvitations = Array.isArray(action.payload?.data)
                    ? action.payload?.data
                    : [action.payload?.data];

                state.invitations = [...state.invitations, ...newInvitations];
            })

        builder
            .addCase(getAllInvitations.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllInvitations.fulfilled, (state, action) => {
                state.invitations = action.payload?.data || [];
                state.loading = false;
            })
            .addCase(getAllInvitations.rejected, (state) => {
                state.loading = false;
            });
    }
});

export const {
    setInviteLinkUserDetails
} = invitationSlice.actions;

export default invitationSlice.reducer;


/**
 * @async function: to add or create invitations for an event
 * 
 * @param {Object} param0 - Object containing eventId and payload
 * @param {number} param0.eventId - ID of the event to which invitations are to be added
 * @param {Object} param0.payload - Payload containing type and invites array
 * @param {string} param0.payload.type - Type of invitee (e.g., 'attendee')
 * @param {Array} param0.payload.invites - Array of invitee objects with email, name, and phone
 * 
 * @returns {Object} - Returns the response from the backend API upon successful creation of invitations
 * 
 * Handles errors and returns the API response
 * 
 * @example of usage: dispatch(createInvitations({ eventId: 123, payload: { type: 'attendee', invites: [{email: '', phone: '', name: ''},...] } }))
 */
export const createInvitations = createAsyncThunk(
    'invitation/createInvitations',
    async ({ eventId, payload }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/events/${eventId}/invitations`, payload);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)


/**
 * @async function: to fetch all invitations for a specific event
 * 
 * @param {number} eventId - ID of the event for which invitations are to be fetched
 * 
 * @returns {Object} - Returns the response from the backend API containing the list of invitations
 * Handles errors and returns the API response
 *
 * @example of usage: dispatch(getAllInvitations(123))
 */
export const getAllInvitations = createAsyncThunk(
    'invitation/getAllInvitations',
    async (eventId, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.get(`/api/v2/events/${eventId}/invitations/attendee`);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)


/**
 * @async function: to validate an invitation code
 * 
 * @param {string} inviteCode - The invitation code to be validated
 * 
 * @returns {Object} - Returns the response from the backend API regarding the validity of the invitation code
 * Handles errors and returns the API response
 * 
 * @example of usage: dispatch(validateInviteCode('INVITE123'))
 */
export const validateInviteCode = createAsyncThunk(
    'invitation/validateInviteCode',
    async (inviteCode, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/invitations/${inviteCode}/authenticate`);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status,
                message: backendResponse?.statusText
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status,
                message: error?.statusText
            });
        }
    }
)


/**
 * @async function: to respond to an invitation with sessionId
 * 
 * @param {Object} param0 - Object containing sessionId
 * @param {string} param0.sessionId - The session ID associated with the invitation response
 * 
 * @returns {Object} - Returns the response from the backend API regarding the invitation response
 * Handles errors and returns the API response
 * 
 * @example of usage: dispatch(respondToUser('SESSION123'))
 */
export const respondToUser = createAsyncThunk(
    'invitation/respondToUser',
    async ({ sessionId }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/invitation/respond-with-user`, {}, {
                headers: {
                    "X-Session-ID": sessionId
                }
            });

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status,
                message: backendResponse?.statusText
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status,
                message: error?.statusText
            });
        }
    }
)